import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pause, Play } from 'lucide-react';
import { systemApi, type TenantDetail } from '@/api';
import { useToast, StatusPill, ConfirmDestructive } from '@/components/ui';

// Full customer detail — Overview, Subscription, Usage, Users, API keys,
// Invoices, Support + read-only "coming next" slots (SSO/Domains/Backups/
// Compliance). The super-admin's per-customer console.
export function CustomerDetail({ tenantId, onBack }: { tenantId: string; onBack: () => void }) {
  const { t: tr } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['tenant-detail', tenantId], queryFn: () => systemApi.tenantDetail(tenantId) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['tenant-detail', tenantId] });
  const toast = useToast();
  const flash = (m: string) => toast.ok(m);
  const [suspendOpen, setSuspendOpen] = useState(false);

  if (isLoading || !data) return <div className="p-6 text-sm text-muted">{tr('customerDetail.loading')}</div>;
  const t = data.tenant;
  const suspended = t.status === 'suspended';

  return (
    <div className="space-y-5">
      <button onClick={onBack} className="text-sm text-muted hover:text-fg">{tr('customerDetail.backToCustomers')}</button>
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t.name}</h1>
          <p className="mt-0.5 flex items-center gap-2 text-sm text-muted">{t.slug} · {t.tenantType} · <StatusPill status={t.status} /></p>
        </div>
        <div className="flex gap-2">
          {/* Enable / Disable — suspend locks users out without deleting data. */}
          {suspended
            ? <button className="tk-btn-ghost text-ok" onClick={async () => { try { await systemApi.unsuspend(t.id); refresh(); toast.ok(tr('customerDetail.toast.enabled', { name: t.name })); } catch { toast.error(tr('customerDetail.toast.enableFailed')); } }}><Play size={15} /> {tr('customerDetail.actions.enable')}</button>
            : <button className="tk-btn-ghost text-warning" onClick={() => setSuspendOpen(true)}><Pause size={15} /> {tr('customerDetail.actions.disable')}</button>}
          <ImpersonateButton tenantId={t.id} name={t.name} onFlash={flash} />
        </div>
      </header>

      {suspendOpen && (
        <ConfirmDestructive
          title={tr('customerDetail.disable.title', { name: t.name })}
          confirmKey={t.name}
          confirmLabel={tr('customerDetail.disable.confirmLabel')}
          onCancel={() => setSuspendOpen(false)}
          onConfirm={async () => {
            try { await systemApi.suspend(t.id); refresh(); toast.warn(tr('customerDetail.toast.disabled', { name: t.name })); setSuspendOpen(false); }
            catch { toast.error(tr('customerDetail.toast.disableFailed')); }
          }}
          body={<>{tr('customerDetail.disable.bodyStart')} <b>{t.name}</b>{tr('customerDetail.disable.bodyMid')} <b>{tr('customerDetail.disable.bodyNothing')}</b>{tr('customerDetail.disable.bodyEnd')}</>}
        />
      )}

      <BillingSummaryCard detail={data} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Overview detail={data} onSaved={() => { refresh(); flash(tr('customerDetail.toast.saved')); }} />
        <SubscriptionCard detail={data} onSaved={() => { refresh(); flash(tr('customerDetail.toast.subscriptionUpdated')); }} />
        <UsageCard detail={data} />
        <UsersCard tenantId={t.id} onFlash={flash} />
        <ApiKeysCard tenantId={t.id} keys={data.apiKeys} onChanged={refresh} onFlash={flash} />
        <InvoicesCard tenantId={t.id} onFlash={flash} />
        <FeatureFlagsCard tenantId={t.id} flags={data.featureFlags} flagStatus={data.flagStatus} onSaved={() => { refresh(); flash(tr('customerDetail.toast.flagsSaved')); }} />
        <SlotsCard />
      </div>
    </div>
  );
}

function Card({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="tk-card tk-card-hover p-4">
      <div className="mb-3 flex items-center justify-between"><div className="text-sm font-medium">{title}</div>{action}</div>
      {children}
    </div>
  );
}

const fmtMoney = (cents: number | null | undefined, currency = 'USD') =>
  cents == null ? '—' : new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(cents / 100);
const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

// At-a-glance billing summary for the customer: current plan, monthly price,
// billing status, renewal/trial date, and lifetime invoiced — read-only, pulled
// from the subscription + invoices (the editable detail stays in Subscription).
function BillingSummaryCard({ detail }: { detail: TenantDetail }) {
  const { t } = useTranslation(['admin', 'common']);
  const { data: invoices } = useQuery({ queryKey: ['invoices', detail.tenant.id], queryFn: () => systemApi.invoices(detail.tenant.id) });
  const s = detail.subscription;
  const currency = s?.currency ?? 'USD';
  const invoiced = (invoices ?? []).reduce((sum, iv) => sum + (iv.amountCents ?? 0), 0);
  const paidCount = (invoices ?? []).filter((iv) => iv.status === 'paid' || iv.status === 'issued').length;
  const renewalLabel = s?.status === 'trial' ? t('customerDetail.billing.trialEnds') : t('customerDetail.billing.renews');
  const renewalDate = s?.status === 'trial' ? s?.trialEndsAt : s?.renewsAt;

  const Stat = ({ label, value, tone }: { label: string; value: React.ReactNode; tone?: string }) => (
    <div className="rounded-lg bg-white/5 p-3 ring-1 ring-white/10">
      <div className="text-[11px] text-muted">{label}</div>
      <div className={`mt-0.5 tk-num text-lg font-semibold ${tone ?? 'text-fg'}`}>{value}</div>
    </div>
  );

  return (
    <div className="tk-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm font-medium">{t('customerDetail.billing.title')}</div>
        {s ? <StatusPill status={s.status} /> : <span className="text-xs text-muted">{t('customerDetail.billing.noSubscription')}</span>}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Stat label={t('customerDetail.billing.plan')} value={<span className="capitalize">{s?.plan ?? '—'}</span>} />
        <Stat label={t('customerDetail.billing.monthlyPrice')} value={fmtMoney(s?.priceCents, currency)} tone="text-ok" />
        <Stat label={renewalLabel} value={fmtDate(renewalDate)} tone={s?.status === 'trial' ? 'text-warning' : undefined} />
        <Stat label={t('customerDetail.billing.invoicedToDate')} value={fmtMoney(invoiced, currency)} />
        <Stat label={t('customerDetail.billing.invoicesIssued')} value={paidCount} />
      </div>
      {!s && <p className="mt-3 text-xs text-muted">{t('customerDetail.billing.noSubscriptionHint')}</p>}
    </div>
  );
}

function Overview({ detail, onSaved }: { detail: TenantDetail; onSaved: () => void }) {
  const { t: tr } = useTranslation(['admin', 'common']);
  const t = detail.tenant;
  const [name, setName] = useState(t.name);
  const [tier, setTier] = useState(t.tier);
  const [tenantType, setTenantType] = useState(t.tenantType);
  const [customDomain, setCustomDomain] = useState(t.customDomain ?? '');
  // Region is fixed at provisioning (the DB + evidence physically live there);
  // the API rejects changes, so it's shown read-only here, not edited.
  const save = async () => { await systemApi.editTenant(t.id, { name, tier, tenantType, customDomain }); onSaved(); };
  const inp = 'tk-input w-full py-1 text-sm';
  return (
    <Card title={tr('customerDetail.overview.title')} action={<button className="tk-btn-primary px-2 py-1 text-xs" onClick={save}>{tr('common:actions.save')}</button>}>
      <div className="grid grid-cols-2 gap-3 text-xs text-muted">
        <label>{tr('customerDetail.overview.name')}<input className={inp} value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label>{tr('customerDetail.overview.tier')}<input className={inp} value={tier} onChange={(e) => setTier(e.target.value)} /></label>
        <label>{tr('customerDetail.overview.tenantType')}<select className={inp} value={tenantType} onChange={(e) => setTenantType(e.target.value)}>{['shared', 'dedicated', 'enterprise'].map((x) => <option key={x} value={x}>{x}</option>)}</select></label>
        <label>{tr('customerDetail.overview.regionFixed')}<input className={`${inp} opacity-70`} value={t.region ?? '—'} readOnly title={tr('customerDetail.overview.regionTitle')} /></label>
        <label className="col-span-2">{tr('customerDetail.overview.customDomain')}<input className={inp} value={customDomain} onChange={(e) => setCustomDomain(e.target.value)} placeholder="portal.acme.com" /></label>
      </div>
      <dl className="mt-3 space-y-1 border-t border-white/10 pt-3 text-xs text-muted">
        <div className="flex justify-between"><dt>{tr('customerDetail.overview.database')}</dt><dd className="tk-num text-fg">{t.databaseName}</dd></div>
        <div className="flex justify-between"><dt>{tr('customerDetail.overview.identityOrg')}</dt><dd className="tk-num">{t.logtoOrgId ?? '—'}</dd></div>
        <div className="flex justify-between"><dt>{tr('customerDetail.overview.customerCode')}</dt><dd className="tk-num">{t.customerCode ?? '—'}</dd></div>
      </dl>
    </Card>
  );
}

// Plan presets — pick one to fill price + limits (still editable). The coarse
// tenants.tier label stays separate; this is the billed subscription.
const PLAN_PRESETS: Record<string, { priceCents: number; limits: { cameras: number; devices: number; users: number } }> = {
  basic: { priceCents: 4900, limits: { cameras: 25, devices: 50, users: 5 } },
  pro: { priceCents: 14900, limits: { cameras: 100, devices: 200, users: 25 } },
  enterprise: { priceCents: 49900, limits: { cameras: 1000, devices: 2000, users: 200 } },
};

function daysUntil(iso?: string | null): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

function SubscriptionCard({ detail, onSaved }: { detail: TenantDetail; onSaved: () => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const toast = useToast();
  const s = detail.subscription;
  const [plan, setPlan] = useState(s?.plan ?? 'basic');
  const [status, setStatus] = useState(s?.status ?? 'active');
  const [price, setPrice] = useState(s?.priceCents != null ? String(s.priceCents / 100) : '');
  const [currency, setCurrency] = useState(s?.currency ?? 'USD');
  const [trialEnds, setTrialEnds] = useState(s?.trialEndsAt ? s.trialEndsAt.slice(0, 10) : '');
  const lim = s?.limits ?? {};
  const [devices, setDevices] = useState(lim.devices ? String(lim.devices) : '');
  const [cameras, setCameras] = useState(lim.cameras ? String(lim.cameras) : '');
  const [usersL, setUsersL] = useState(lim.users ? String(lim.users) : '');
  const [busy, setBusy] = useState(false);

  const applyPreset = (key: string) => {
    setPlan(key);
    const p = PLAN_PRESETS[key];
    if (p) { setPrice(String(p.priceCents / 100)); setCameras(String(p.limits.cameras)); setDevices(String(p.limits.devices)); setUsersL(String(p.limits.users)); }
  };
  const save = async () => {
    const num = (v: string) => (v.trim() === '' ? undefined : Number(v));
    setBusy(true);
    try {
      await systemApi.setSubscription(detail.tenant.id, {
        plan, status, currency, priceCents: price.trim() === '' ? null : Math.round(Number(price) * 100),
        ...(status === 'trial' && trialEnds ? { trialEndsAt: new Date(trialEnds + 'T23:59:59').toISOString() } : {}),
        limits: { ...(num(devices) != null ? { devices: num(devices)! } : {}), ...(num(cameras) != null ? { cameras: num(cameras)! } : {}), ...(num(usersL) != null ? { users: num(usersL)! } : {}) },
      });
      onSaved();
    } catch { toast.error(t('customerDetail.toast.subscriptionSaveFailed')); }
    finally { setBusy(false); }
  };
  const inp = 'tk-input w-full py-1 text-sm';
  const trialDays = daysUntil(s?.trialEndsAt);

  return (
    <Card title={t('customerDetail.subscription.title')} action={<button className="tk-btn-primary px-2 py-1 text-xs disabled:opacity-50" disabled={busy} onClick={save}>{busy ? t('customerDetail.actions.saving') : t('common:actions.save')}</button>}>
      {/* Trial banner */}
      {s?.status === 'trial' && (
        <div className={`mb-3 rounded-md px-3 py-2 text-xs ring-1 ${trialDays != null && trialDays <= 3 ? 'bg-critical/10 text-critical ring-critical/30' : 'bg-warning/10 text-warning ring-warning/30'}`}>
          {trialDays == null ? t('customerDetail.subscription.trialNoEnd') : trialDays < 0 ? t('customerDetail.subscription.trialExpired') : t('customerDetail.subscription.trialRemaining', { count: trialDays })}
        </div>
      )}
      {/* Plan presets */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {Object.keys(PLAN_PRESETS).map((k) => (
          <button key={k} onClick={() => applyPreset(k)} className={`rounded-full px-2.5 py-1 text-xs capitalize ring-1 ${plan === k ? 'bg-primary/15 text-primary ring-primary/30' : 'bg-white/5 text-muted ring-white/10 hover:text-fg'}`}>{k}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs text-muted">
        <label>{t('customerDetail.subscription.plan')}<input className={inp} value={plan} onChange={(e) => setPlan(e.target.value)} /></label>
        <label>{t('customerDetail.subscription.status')}<select className={inp} value={status} onChange={(e) => setStatus(e.target.value)}>{['trial', 'active', 'past_due', 'cancelled'].map((x) => <option key={x} value={x}>{x.replace('_', ' ')}</option>)}</select></label>
        {status === 'trial' && <label className="col-span-2">{t('customerDetail.subscription.trialEnds')}<input className={inp} type="date" value={trialEnds} onChange={(e) => setTrialEnds(e.target.value)} /></label>}
        <label>{t('customerDetail.subscription.pricePerMonth')}<input className={inp} type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" /></label>
        <label>{t('customerDetail.subscription.currency')}<select className={inp} value={currency} onChange={(e) => setCurrency(e.target.value)}>{['USD', 'EUR', 'ILS', 'GBP'].map((x) => <option key={x} value={x}>{x}</option>)}</select></label>
        <label>{t('customerDetail.subscription.limitCameras')}<input className={inp} type="number" value={cameras} onChange={(e) => setCameras(e.target.value)} placeholder="∞" /></label>
        <label>{t('customerDetail.subscription.limitDevices')}<input className={inp} type="number" value={devices} onChange={(e) => setDevices(e.target.value)} placeholder="∞" /></label>
        <label>{t('customerDetail.subscription.limitUsers')}<input className={inp} type="number" value={usersL} onChange={(e) => setUsersL(e.target.value)} placeholder="∞" /></label>
      </div>
    </Card>
  );
}

function UsageCard({ detail }: { detail: TenantDetail }) {
  const { t } = useTranslation(['admin', 'common']);
  const u = detail.usage; const over = new Set(detail.overLimit);
  const row = (label: string, value: number, key: string) => (
    <div className="flex items-center justify-between border-b border-white/5 py-1.5 last:border-0">
      <span className="text-muted">{label}</span>
      <span className={`tk-num font-medium ${over.has(key) ? 'text-critical' : 'text-fg'}`}>{value}{over.has(key) ? ' ' + t('customerDetail.usage.overLimit') : ''}</span>
    </div>
  );
  return <Card title={t('customerDetail.usage.title')}>{row(t('customerDetail.usage.devices'), u.devices, 'devices')}{row(t('customerDetail.usage.cameras'), u.cameras, 'cameras')}{row(t('customerDetail.usage.edges'), u.edges, 'edges')}{row(t('customerDetail.usage.users'), u.users, 'users')}</Card>;
}

function UsersCard({ tenantId, onFlash }: { tenantId: string; onFlash: (m: string) => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const toast = useToast();
  const { data: users } = useQuery({ queryKey: ['tenant-users', tenantId], queryFn: () => systemApi.tenantUsers(tenantId) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['tenant-users', tenantId] });
  const ROLES = ['tenant_admin', 'operator', 'technician', 'viewer'];
  const [email, setEmail] = useState('');
  const [erase, setErase] = useState<{ id: string; email: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const invite = async () => {
    if (!email) return;
    try {
      const r = await systemApi.inviteAdmin(tenantId, { email, role: 'tenant_admin' });
      setEmail(''); refresh();
      toast.ok(
        r.created && r.emailSent === false
          ? t('customerDetail.toast.inviteEmailFailed', { defaultValue: "Added {{email}}, but the invite email couldn't be sent — resend it.", email: r.email })
          : r.created
            ? t('customerDetail.toast.invited', { defaultValue: 'Invitation sent to {{email}} — they set their own password.', email: r.email })
            : t('customerDetail.toast.userAdded', { email: r.email }),
      );
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg === 'user_limit_reached' ? t('customerDetail.toast.userLimitReached') : t('customerDetail.toast.addUserFailed'));
    }
  };
  const exportUser = async (u: { id: string; email: string }) => {
    try {
      const data = await systemApi.exportUser(tenantId, u.id);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `user-${u.email}-export.json`; a.click();
      toast.ok(t('customerDetail.toast.userExported', { email: u.email }));
    } catch { toast.error(t('customerDetail.toast.exportUserFailed')); }
  };
  const doErase = async () => {
    if (!erase) return;
    setBusy(true);
    try {
      await systemApi.eraseUser(tenantId, erase.id);
      toast.ok(t('customerDetail.toast.userErased', { email: erase.email }));
      setErase(null); refresh();
    } catch { toast.error(t('customerDetail.toast.eraseUserFailed')); }
    finally { setBusy(false); }
  };

  return (
    <Card title={t('customerDetail.users.title')}>
      <div className="mb-3 flex gap-2">
        <input className="tk-input flex-1 py-1 text-sm" placeholder="admin@acme.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <button className="tk-btn-primary px-2 py-1 text-xs" onClick={invite}>{t('customerDetail.users.inviteAdmin')}</button>
      </div>
      <ul className="text-sm">
        {(users ?? []).map((u) => (
          <li key={u.id} className="flex items-center justify-between gap-2 border-b border-white/5 py-1.5 last:border-0">
            <span className="min-w-0"><span className="block truncate text-fg">{u.name}</span><span className="block truncate text-xs text-muted">{u.email}</span></span>
            <span className="flex shrink-0 items-center gap-1">
              <select className="tk-input py-0.5 text-xs" value={ROLES.includes(u.role) ? u.role : 'viewer'} onChange={async (e) => { try { await systemApi.setUserRole(tenantId, u.id, e.target.value); refresh(); toast.ok(t('customerDetail.toast.roleUpdated', { email: u.email })); } catch { toast.error(t('customerDetail.toast.roleChangeFailed')); } }}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <button className="tk-btn-ghost px-1.5 py-0.5 text-xs" title={t('customerDetail.users.exportTitle')} onClick={() => exportUser(u)}>⤓</button>
              <button className="tk-btn-ghost px-1.5 py-0.5 text-xs text-muted" title={t('customerDetail.users.removeTitle')} onClick={async () => { if (confirm(t('customerDetail.users.removeConfirm', { email: u.email }))) { try { await systemApi.removeUser(tenantId, u.id); refresh(); toast.ok(t('customerDetail.toast.userRemoved', { email: u.email })); } catch { toast.error(t('customerDetail.toast.removeUserFailed')); } } }}>✕</button>
              <button className="tk-btn-ghost px-1.5 py-0.5 text-xs text-critical" title={t('customerDetail.users.eraseTitle')} onClick={() => setErase({ id: u.id, email: u.email })}>⌫</button>
            </span>
          </li>
        ))}
        {(users ?? []).length === 0 && <li className="py-3 text-center text-xs text-muted">{t('customerDetail.users.empty')}</li>}
      </ul>
      <p className="mt-2 text-[11px] text-muted">{t('customerDetail.users.legend')}</p>
      {erase && (
        <ConfirmDestructive
          title={t('customerDetail.erase.title', { email: erase.email })}
          confirmKey={erase.email}
          confirmLabel={t('customerDetail.erase.confirmLabel')}
          busy={busy}
          onCancel={() => setErase(null)}
          onConfirm={doErase}
          body={<>{t('customerDetail.erase.bodyStart')} <b>{t('customerDetail.erase.bodyPermanent')}</b> {t('customerDetail.erase.bodyEnd')}</>}
        />
      )}
    </Card>
  );
}

function ApiKeysCard({ tenantId, keys, onChanged, onFlash }: { tenantId: string; keys: TenantDetail['apiKeys']; onChanged: () => void; onFlash: (m: string) => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const [name, setName] = useState('');
  const create = async () => {
    const r = await systemApi.createApiKey(tenantId, name || 'API key');
    setName(''); onChanged();
    onFlash(t('customerDetail.toast.keyCreated', { key: r.key }));
  };
  return (
    <Card title={t('customerDetail.apiKeys.title')}>
      <div className="mb-3 flex gap-2">
        <input className="tk-input flex-1 py-1 text-sm" placeholder={t('customerDetail.apiKeys.namePlaceholder')} value={name} onChange={(e) => setName(e.target.value)} />
        <button className="tk-btn-primary px-2 py-1 text-xs" onClick={create}>{t('customerDetail.apiKeys.issue')}</button>
      </div>
      <ul className="text-sm">
        {keys.filter((k) => !k.revokedAt).map((k) => (
          <li key={k.id} className="flex items-center justify-between border-b border-white/5 py-1.5 last:border-0">
            <span><span className="text-fg">{k.name}</span> <span className="tk-num text-xs text-muted">{k.keyPrefix}…</span></span>
            <button className="tk-btn-ghost px-1.5 py-0.5 text-xs text-critical" onClick={async () => { await systemApi.revokeApiKey(tenantId, k.id); onChanged(); }}>{t('customerDetail.apiKeys.revoke')}</button>
          </li>
        ))}
        {keys.filter((k) => !k.revokedAt).length === 0 && <li className="py-3 text-center text-xs text-muted">{t('customerDetail.apiKeys.empty')}</li>}
      </ul>
    </Card>
  );
}

function InvoicesCard({ tenantId, onFlash }: { tenantId: string; onFlash: (m: string) => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const { data: invoices } = useQuery({ queryKey: ['invoices', tenantId], queryFn: () => systemApi.invoices(tenantId) });
  const gen = async () => {
    const r = await systemApi.generateInvoice(tenantId);
    qc.invalidateQueries({ queryKey: ['invoices', tenantId] });
    onFlash(t('customerDetail.toast.invoiceGenerated', { amount: (r.amountCents / 100).toFixed(2), sink: r.sink ? ' (sink)' : '' }));
  };
  return (
    <Card title={t('customerDetail.invoices.title')} action={<button className="tk-btn-primary px-2 py-1 text-xs" onClick={gen}>{t('customerDetail.invoices.generate')}</button>}>
      <ul className="text-sm">
        {(invoices ?? []).map((iv) => (
          <li key={iv.id} className="flex items-center justify-between border-b border-white/5 py-1.5 last:border-0">
            <span className="text-muted">{new Date(iv.periodStart).toLocaleDateString()} · {iv.status}</span>
            <span className="tk-num text-fg">{iv.amountCents != null ? (iv.amountCents / 100).toFixed(2) : '—'} {iv.currency}</span>
          </li>
        ))}
        {(invoices ?? []).length === 0 && <li className="py-3 text-center text-xs text-muted">{t('customerDetail.invoices.empty')}</li>}
      </ul>
    </Card>
  );
}

function FeatureFlagsCard({ tenantId, flags, flagStatus, onSaved }: {
  tenantId: string;
  flags: Record<string, boolean>;
  flagStatus?: Record<string, { enforced: boolean; gates: string }>;
  onSaved: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const KNOWN = ['betaDashboards', 'aiInsights', 'videoWall', 'mobileApp'];
  // An ENFORCED flag defaults ON when unset (opt-out — the feature works today,
  // so an unset flag must not read as "off"). Unwired flags show as off.
  const defaultFor = (k: string) => !!flagStatus?.[k]?.enforced;
  const [state, setState] = useState<Record<string, boolean>>({
    ...Object.fromEntries(KNOWN.map((k) => [k, defaultFor(k)])),
    ...flags,
  });
  const toggle = async (k: string) => { const next = { ...state, [k]: !state[k] }; setState(next); await systemApi.setFlags(tenantId, next); onSaved(); };
  return (
    <Card title={t('customerDetail.featureFlags.title')}>
      <ul className="text-sm">
        {KNOWN.map((k) => {
          // CFG-04: flags used to be echo-only. Say plainly which ones the
          // runtime actually enforces so a toggle can't imply a lie.
          const enforced = !!flagStatus?.[k]?.enforced;
          return (
            <li key={k} className="flex items-center justify-between border-b border-white/5 py-1.5 last:border-0">
              <span className="min-w-0">
                <span className="text-muted">{k}</span>
                {!enforced && (
                  <span className="ms-2 rounded-full bg-white/5 px-1.5 py-0.5 text-[10px] text-muted ring-1 ring-white/10">
                    {t('customerDetail.featureFlags.notEnforced')}
                  </span>
                )}
                {enforced && flagStatus?.[k]?.gates && (
                  <span className="block truncate text-[11px] text-muted/70">{flagStatus[k].gates}</span>
                )}
              </span>
              <button
                onClick={() => toggle(k)}
                disabled={!enforced}
                title={enforced ? undefined : t('customerDetail.featureFlags.notEnforcedHint')}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${state[k] ? 'bg-primary' : 'bg-white/10 ring-1 ring-white/15'} ${enforced ? '' : 'cursor-not-allowed opacity-40'}`}
              >
                <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition ${state[k] ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function SlotsCard() {
  const { t } = useTranslation(['admin', 'common']);
  return (
    <Card title={t('customerDetail.comingNext.title')}>
      <ul className="space-y-1.5 text-xs text-muted">
        <li>{t('customerDetail.comingNext.sso')}</li>
        <li>{t('customerDetail.comingNext.domainVerification')}</li>
        <li>{t('customerDetail.comingNext.backups')}</li>
        <li>{t('customerDetail.comingNext.compliance')}</li>
        <li>{t('customerDetail.comingNext.mfa')}</li>
      </ul>
    </Card>
  );
}

// Impersonation — a proper glass modal (not a browser prompt): captures the
// audited reason, then shows the issued token with copy + expiry so the operator
// can paste it into the operator app.
function ImpersonateButton({ tenantId, name, onFlash }: { tenantId: string; name: string; onFlash: (m: string) => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ token: string; expiresIn: number } | null>(null);

  const close = () => { setOpen(false); setReason(''); setResult(null); setBusy(false); };
  const submit = async () => {
    if (reason.trim().length < 3) return;
    setBusy(true);
    try {
      const r = await systemApi.impersonate(tenantId, reason.trim());
      setResult({ token: r.accessToken, expiresIn: r.expiresIn });
      onFlash(t('customerDetail.toast.impersonationIssued', { name, minutes: Math.round(r.expiresIn / 60) }));
    } catch {
      toast.error(t('customerDetail.toast.impersonationFailed', { name }));
    } finally { setBusy(false); }
  };
  const copy = async () => {
    if (!result) return;
    try { await navigator.clipboard.writeText(result.token); toast.ok(t('customerDetail.toast.tokenCopied')); }
    catch { toast.warn(t('customerDetail.toast.copyFailed')); }
  };

  return (
    <>
      <button className="tk-btn-ghost" onClick={() => setOpen(true)}>{t('customerDetail.impersonate.button')}</button>
      {open && (
        <div className="tk-modal-overlay" onClick={close}>
          <div className="tk-modal w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold">{t('customerDetail.impersonate.modalTitle', { name })}</h2>
              <button className="tk-btn-ghost px-2" onClick={close}>✕</button>
            </div>

            {!result ? (
              <>
                <p className="mb-3 text-sm text-muted">
                  {t('customerDetail.impersonate.introStart')} <b>{t('customerDetail.impersonate.introAsCustomer')}</b> {t('customerDetail.impersonate.introMid')} <b>{t('customerDetail.impersonate.introAudited')}</b>{t('customerDetail.impersonate.introEnd')}
                </p>
                <label className="mb-1 block text-xs text-muted">{t('customerDetail.impersonate.reasonLabel')}</label>
                <input
                  className="tk-input" value={reason} autoFocus
                  onChange={(e) => setReason(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                  placeholder={t('customerDetail.impersonate.reasonPlaceholder')}
                />
                <div className="mt-4 flex justify-end gap-2">
                  <button className="tk-btn-ghost" onClick={close} disabled={busy}>{t('common:actions.cancel')}</button>
                  <button className="tk-btn-primary" onClick={submit} disabled={busy || reason.trim().length < 3}>
                    {busy ? t('customerDetail.impersonate.issuing') : t('customerDetail.impersonate.start')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="mb-3 flex items-center gap-2 text-sm text-ok">{t('customerDetail.impersonate.issued', { minutes: Math.round(result.expiresIn / 60) })}</p>
                <label className="mb-1 block text-xs text-muted">{t('customerDetail.impersonate.tokenLabel')}</label>
                <textarea readOnly className="tk-input h-24 w-full resize-none font-mono text-[11px]" value={result.token} onFocus={(e) => e.currentTarget.select()} />
                <p className="mt-2 text-[11px] text-muted">{t('customerDetail.impersonate.tokenHint', { name })}</p>
                <div className="mt-4 flex justify-end gap-2">
                  <button className="tk-btn-ghost" onClick={close}>{t('customerDetail.impersonate.done')}</button>
                  <button className="tk-btn-primary" onClick={copy}>{t('customerDetail.impersonate.copyToken')}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
